import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  Appointment,
  AppointmentCount,
  AppointmentDetailsResponse,
  LoadAppointmentDetailsPayload,
  LoadAppointmentsPayload,
} from "./types";

interface AsyncState<T> {
  data: T | null;
  loading: boolean;
  error: boolean;
}

export interface AppointmentsState {
  count: AsyncState<AppointmentCount>;
  list: AsyncState<Appointment[]>;
  details: AsyncState<AppointmentDetailsResponse>;
}

export const initialState: AppointmentsState = {
  count: { data: null, loading: false, error: false },
  list: { data: null, loading: false, error: false },
  details: { data: null, loading: false, error: false },
};

export const appointmentsSlice = createSlice({
  name: "appointments",
  initialState,
  reducers: {
    loadCount(state) {
      state.count.loading = true;
      state.count.error = false;
    },
    countLoaded(state, { payload }: PayloadAction<AppointmentCount>) {
      state.count.data = payload;
      state.count.loading = false;
      state.count.error = false;
    },
    loadCountError(state) {
      state.count.loading = false;
      state.count.error = true;
    },

    loadAppointments(
      state,
      _action: PayloadAction<LoadAppointmentsPayload | undefined>,
    ) {
      state.list.loading = true;
      state.list.error = false;
    },
    appointmentsLoaded(state, { payload }: PayloadAction<Appointment[]>) {
      state.list.data = payload;
      state.list.loading = false;
      state.list.error = false;
    },
    loadAppointmentsError(state) {
      state.list.loading = false;
      state.list.error = true;
    },
    setAppointmentDone(
      state,
      { payload }: PayloadAction<{ key: string; isDone: boolean }>,
    ) {
      const record = state.list.data?.find((a) => a.key === payload.key);
      if (record) record.isDone = payload.isDone;
    },

    loadAppointmentDetails(
      state,
      _action: PayloadAction<LoadAppointmentDetailsPayload>,
    ) {
      state.details.loading = true;
      state.details.error = false;
      state.details.data = null;
    },
    appointmentDetailsLoaded(
      state,
      { payload }: PayloadAction<AppointmentDetailsResponse>,
    ) {
      state.details.data = payload;
      state.details.loading = false;
      state.details.error = false;
    },
    loadAppointmentDetailsError(state) {
      state.details.loading = false;
      state.details.error = true;
    },
    appointmentDetailsCleared(state) {
      state.details = { data: null, loading: false, error: false };
    },
  },
});

export const {
  loadCount,
  countLoaded,
  loadCountError,
  loadAppointments,
  appointmentsLoaded,
  loadAppointmentsError,
  setAppointmentDone,
  loadAppointmentDetails,
  appointmentDetailsLoaded,
  loadAppointmentDetailsError,
  appointmentDetailsCleared,
} = appointmentsSlice.actions;

export const { reducer } = appointmentsSlice;
